import axios from 'axios';
import config from '../config';
import { clearAuthSession, getJwtToken } from './authentication';

let redirecting = false;

const LOGIN_PATH = '/';

const isUnauthenticatedMessage = (message) =>
  typeof message === 'string' && /unauthenticated|غير مصرح/i.test(message);

/**
 * True for API 401s or Laravel-style `{ message: "Unauthenticated." }` bodies.
 */
export function isUnauthenticatedError(error) {
  if (error?.response?.status === 401) {
    return true;
  }

  const message =
    error?.response?.data?.message
    ?? error?.message
    ?? error?.formattedMessage;

  return isUnauthenticatedMessage(message);
}

/**
 * Drop the local session and send the user to the login screen when the API
 * rejects their token.
 */
export function handleUnauthorizedResponse() {
  if (typeof window === 'undefined' || redirecting || !getJwtToken()) {
    return;
  }

  redirecting = true;
  clearAuthSession();
  window.location.replace(LOGIN_PATH);
}

const resolveRequestUrl = (input) => {
  if (typeof input === 'string') {
    return input;
  }

  if (input?.url) {
    return input.url;
  }

  return null;
};

const isApiRequest = (input) => {
  const url = resolveRequestUrl(input);
  if (!url) {
    return false;
  }

  const bases = [config.apiRootUrl, config.apiUrl].filter(Boolean);
  return bases.some((base) => url === base || url.startsWith(`${base}/`) || url.startsWith(base));
};

const isLoginRequest = (input) => {
  const url = resolveRequestUrl(input);
  if (!url) {
    return false;
  }

  return /\/backoffice\/login(?:\?|$)/i.test(url) || /\/login(?:\?|$)/i.test(url);
};

export function installSessionGuard() {
  if (typeof window === 'undefined' || window.__dentristSessionGuardInstalled) {
    return;
  }

  window.__dentristSessionGuardInstalled = true;

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (isUnauthenticatedError(error) && !isLoginRequest(error?.config)) {
        handleUnauthorizedResponse();
      }

      return Promise.reject(error);
    },
  );

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input, init) => {
    const response = await originalFetch(input, init);

    if (response.status === 401 && isApiRequest(input) && !isLoginRequest(input)) {
      handleUnauthorizedResponse();
    }

    return response;
  };
}
